import React from 'react';
import { Color, SpaceScale, withCitrusTheme } from '@gousto-internal/citrus-react';

export type MediaPlayPauseButtonSize = 'sm' | 'md';

const SIZE_SCALE: Record<MediaPlayPauseButtonSize, SpaceScale> = { sm: 8, md: 10 };
const EDGE_SCALE: Record<MediaPlayPauseButtonSize, SpaceScale> = { sm: 3, md: 4 };

type ControlButtonProps = React.JSX.IntrinsicElements['button'] & {
  buttonSize: MediaPlayPauseButtonSize;
};

const ControlButton = withCitrusTheme<'button', ControlButtonProps>(
  'button',
  (theme, props) => ({
    position: 'absolute',
    top: theme.spacing(EDGE_SCALE[props.buttonSize]),
    right: theme.spacing(EDGE_SCALE[props.buttonSize]),
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(SIZE_SCALE[props.buttonSize]),
    height: theme.spacing(SIZE_SCALE[props.buttonSize]),
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    pointerEvents: 'auto',
    backgroundColor: theme.color(Color.White),
    color: theme.color(Color.ColdGrey_800),
    boxShadow: '0px 4px 12px -2px rgba(0, 0, 0, 0.45)',
    '&:hover': { backgroundColor: theme.color(Color.ColdGrey_50) },
    '&:focus-visible': {
      outline: `2px solid ${theme.color(Color.Secondary_600)}`,
      outlineOffset: theme.spacing(0.5),
    },
  }),
  { omitProps: ['buttonSize'] },
);

function PauseGlyph(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <rect x="2" y="1" width="3.5" height="12" rx="1" />
      <rect x="8.5" y="1" width="3.5" height="12" rx="1" />
    </svg>
  );
}

function PlayGlyph(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <path d="M3 1.5v11l9-5.5z" />
    </svg>
  );
}

export type MediaPlayPauseButtonProps = {
  readonly isPlaying: boolean;
  readonly onToggle: () => void;
  readonly size?: MediaPlayPauseButtonSize;
  readonly label?: string;
  readonly showPressed?: boolean;
};

export function MediaPlayPauseButton({
  isPlaying,
  onToggle,
  size = 'md',
  label,
  showPressed = true,
}: MediaPlayPauseButtonProps): JSX.Element {
  return (
    <ControlButton
      type="button"
      buttonSize={size}
      aria-label={label ?? (isPlaying ? 'Pause' : 'Play')}
      aria-pressed={showPressed ? isPlaying : undefined}
      onClick={onToggle}
    >
      {isPlaying ? <PauseGlyph /> : <PlayGlyph />}
    </ControlButton>
  );
}
